import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { showToast } from '@ui/components/elements/ToastManager'
import { getInitials, profileColors } from '@ui/lib/utils'
import { useAuth } from '@ui/providers/AuthProvider'
import { useTelemetryStore } from '@ui/stores'
import { useClickAway } from '@uidotdev/usehooks'
import clsx from 'clsx'

export const UserMenu = () => {
  const navigate = useNavigate()
  const auth = useAuth()
  const { currentMember, currentOrganization } = auth

  const userMenuRef = useClickAway(() => {
    setIsUserMenuOpen(false)
  })

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)

  const resetTelemetryStore = useTelemetryStore(useShallow((state) => state.resetStore))

  const handleSignOut = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    resetTelemetryStore()

    event.preventDefault()
    auth.signout((err) => {
      if (err) {
        showToast.error(err?.message || 'An error occurred. Please try again.')
      } else {
        navigate('/login')
      }
    })
  }

  const getName = () => {
    if (currentMember?.email.endsWith('@datable.io') && currentOrganization?.name) {
      return currentOrganization?.name
    } else if (currentMember?.first_name) {
      return currentMember?.first_name
    } else if (currentOrganization?.name) {
      return currentOrganization?.name
    } else {
      return 'User'
    }
  }

  const generateProfile = (name: string) => {
    const randomColor = profileColors[Math.floor(Math.random() * profileColors.length)]

    return {
      initials: getInitials(name) ?? 'DB',
      bgColor: randomColor,
    }
  }

  const profile = useMemo(() => generateProfile(getName()), [getName()])

  return (
    <div
      className="relative flex w-fit cursor-pointer flex-row items-center justify-center gap-2"
      ref={userMenuRef as React.RefObject<HTMLDivElement>}
      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
      <div className={clsx('size-10 flex flex-row items-center justify-center rounded-full', profile.bgColor)}>
        <p>{profile.initials}</p>
      </div>
      <ChevronDownIcon
        className={clsx('size-4 stroke-gray-900 transition-all duration-300', { 'rotate-180': isUserMenuOpen })}
      />

      {isUserMenuOpen && (
        <div className="absolute right-0 top-11 z-40 h-auto w-48 rounded-md border border-gray-200 bg-white p-2 shadow-md">
          <div className="flex flex-col gap-1">
            <div onClick={() => navigate('/profile')} className="cursor-pointer rounded-md px-2 py-1 hover:bg-gray-100">
              <p className="text-sm font-medium">Profile</p>
            </div>
            <div
              onClick={() => navigate('/organizations')}
              className="cursor-pointer rounded-md px-2 py-1 hover:bg-gray-100">
              <p className="text-sm font-medium">Change organization</p>
            </div>
            <div
              onClick={() => navigate(`/manage/${currentOrganization?.id}/instances`)}
              className="cursor-pointer rounded-md px-2 py-1 hover:bg-gray-100">
              <p className="text-sm font-medium">Change Instance</p>
            </div>
            <div onClick={handleSignOut} className="cursor-pointer rounded-md px-2 py-1 hover:bg-gray-100">
              <p className="text-sm font-medium">Sign out</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
